/**
 * Users list page.
 * 
 * @module pages/UsersPage
 * @see CSDEV-145 - Users list: add business count + last active columns
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, SearchInput, Pagination, Badge, Card } from '../components/ui';
import { useUsers, usePlans, usePagination, useAuditLog, useExclusionRules, type UserWithPlanExtended } from '../hooks';
import { isExcludedUser } from '../config/excluded-users';
import { AuditActionTypes } from '../lib/audit';
import { formatDate, formatDateTime } from '../lib/utils';
import type { DataTableColumn } from '../types';

/**
 * Users list page component.
 * 
 * @returns Users page element
 */
export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [internalFilter, setInternalFilter] = useState<'all' | 'external' | 'internal'>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { page, pageSize, goToPage, changePageSize, pageSizeOptions } = usePagination();
  const { logAction } = useAuditLog();

  const { data: plans } = usePlans();
  const { data: exclusionRules, isLoading: exclusionRulesLoading } = useExclusionRules();
  // When internal/external filter is active, fetch all users so the client-side
  // filter operates on the full dataset instead of a single page.
  // Cap at 10,000 rows — if the user table exceeds this, move exclusion
  // filtering server-side (e.g., a database view or RPC).
  const isFilteringByInternal = internalFilter !== 'all';
  const isPaidFilter = planFilter === '__paid__';
  const paidPlanIds = useMemo(
    () => plans?.filter((p) => p.paid_plan).map((p) => p.id) ?? [],
    [plans]
  );

  // Reset status filter when leaving "All Paid Plans" mode
  useEffect(() => {
    if (!isPaidFilter) setStatusFilter('');
  }, [isPaidFilter]);
  const { data, isLoading, error } = useUsers({
    page: isFilteringByInternal ? 1 : page,
    pageSize: isFilteringByInternal ? 10000 : pageSize,
    search,
    planId: !isPaidFilter && planFilter ? planFilter : undefined,
    planIds: isPaidFilter ? paidPlanIds : undefined,
    subscriptionStatus: isPaidFilter ? 'active' : (statusFilter || undefined),
    sortBy,
    sortOrder,
  });

  /**
   * Handles column sort clicks.
   * Toggles order if same column, resets to desc for new column.
   * @see CSDEV-147 - Wire up DataTable sorting
   */
  const handleSort = useCallback((key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    goToPage(1);
  }, [sortBy, sortOrder, goToPage]);

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_USERS_LIST);
  }, [logAction]);

  // Log search
  useEffect(() => {
    if (search) {
      logAction(AuditActionTypes.SEARCH_USERS, undefined, undefined, { search });
    }
  }, [search, logAction]);

  const handleRowClick = useCallback(
    (user: UserWithPlanExtended) => {
      navigate(`/users/${user.id}`);
    },
    [navigate]
  );

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    goToPage(1);
  }, [goToPage]);

  // Client-side filter for internal/external users
  const filteredData = data?.data.filter((user) => {
    if (internalFilter === 'all') return true;
    const excluded = isExcludedUser(user.email, exclusionRules);
    return internalFilter === 'internal' ? excluded : !excluded;
  });

  const columns: DataTableColumn<UserWithPlanExtended>[] = [
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (user) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{user.email}</span>
          {isExcludedUser(user.email, exclusionRules) && (
            <Badge variant="neutral" size="sm">Internal</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'full_name',
      header: 'Name',
      render: (user) => user.full_name || '—',
    },
    {
      key: 'plans',
      header: 'Plan',
      render: (user) => (
        <Badge variant="info" size="sm">
          {user.plans?.name || 'No plan'}
        </Badge>
      ),
    },
    {
      key: 'subscription_status',
      header: 'Status',
      render: (user) => {
        const status = user.subscription_status;
        const variant = 
          status === 'active' ? 'success' :
          status === 'trialing' ? 'info' :
          status === 'past_due' ? 'warning' :
          status === 'canceled' ? 'error' :
          'neutral';
        return (
          <Badge variant={variant} size="sm">
            {status || 'None'}
          </Badge>
        );
      },
    },
    {
      key: 'business_count',
      header: 'Businesses',
      align: 'center',
      render: (user) => (
        <span className="text-gray-600 font-medium">{user.business_count ?? 0}</span>
      ),
    },
    {
      key: 'last_activity',
      header: 'Last Active',
      sortable: true,
      render: (user) => (
        <span className="text-gray-500 text-sm">
          {user.last_activity ? formatDateTime(user.last_activity) : '—'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      sortable: true,
      render: (user) => (
        <span className="text-gray-500">{formatDate(user.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <p className="text-gray-500">
          View all user accounts and subscription information
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <SearchInput
            placeholder="Search by email or name..."
            onSearch={handleSearch}
            className="flex-1 max-w-md"
          />
          <select
            value={planFilter}
            onChange={(e) => {
              setPlanFilter(e.target.value);
              goToPage(1);
            }}
            className="input w-full sm:w-48"
          >
            <option value="">All plans</option>
            <option value="__paid__">All Paid Plans</option>
            {plans?.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
          <select
            value={isPaidFilter ? 'active' : statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              goToPage(1);
            }}
            disabled={isPaidFilter}
            className="input w-full sm:w-48"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="past_due">Past Due</option>
            <option value="canceled">Canceled</option>
          </select>
          <select
            value={internalFilter}
            onChange={(e) => {
              setInternalFilter(e.target.value as 'all' | 'external' | 'internal');
              goToPage(1);
            }}
            disabled={exclusionRulesLoading}
            className="input w-full sm:w-48"
          >
            <option value="all">All users</option>
            <option value="external">External only</option>
            <option value="internal">Internal only</option>
          </select>
        </div>
      </Card>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load users: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredData || []}
          isLoading={isLoading}
          emptyMessage="No users found"
          onRowClick={handleRowClick}
          keyExtractor={(user) => user.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
        {data && isFilteringByInternal ? (
          <div className="px-4 py-3 text-sm text-gray-500 border-t border-gray-200">
            {filteredData?.length ?? 0} {internalFilter} user{(filteredData?.length ?? 0) !== 1 ? 's' : ''} found
          </div>
        ) : data && data.totalPages > 0 ? (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={pageSizeOptions}
          />
        ) : null}
      </Card>
    </div>
  );
}
